import { useState, useEffect, useMemo, useCallback } from "react";
import {
  useCreateProfilingMaster,
  useOneProfilingMaster,
  useUpdateProfilingMaster,
} from "http-hooks/profilingMaster/useProfilingMaster";
import { useAllDiscounts } from "http-hooks/discount/useDiscount";
import useAuth from "hooks/useAuth";

let rowIdCounter = 0;
const nextRowId = () => ++rowIdCounter;

const defaultProfileType = { value: "V", label: "Vendor" };

export const useProfilingMasterForm = (id, onClose) => {
  const { agttp } = useAuth();

  const [profileType, setProfileType] = useState(defaultProfileType);
  const [selectedParty, setSelectedParty] = useState(null);

  const [profilingDetails, setProfilingDetails] = useState({
    ps_Id: "",
    date: new Date().toISOString().split("T")[0],
    noofdaysCD: 0,
    noofdayspay: 0,
    intprcnt: 0,
    terms: "",
    agentcom: 0,
    agencommcond: "",
    defaultextn: 0,
    ag_status: 1,
  });

  // Each section holds an array of row objects:
  // { _rowId, discount_id, percentage_value, amount_value }
  const [annualRows, setAnnualRows] = useState([]);
  const [billRows, setBillRows] = useState([]);
  const [afterTaxRows, setAfterTaxRows] = useState([]);

  const { isLoading: isLoadingProfiling, data } = useOneProfilingMaster(id);
  const { mutateAsync: mutateCreate, isPending: isCreating } = useCreateProfilingMaster();
  const { mutateAsync: mutateUpdate, isPending: isUpdating } = useUpdateProfilingMaster();

  // Agents and trader vendor profiles use purchase (P) discounts.
  // Trader customer profiles use sales (S) discounts.
  const discountType =
    agttp === 0 || profileType?.value === "V" ? "P" : "S";
  const { data: allDiscounts = [], isLoading: isLoadingDiscounts } = useAllDiscounts({
    ag_status: 1,
    type: discountType,
  });

  const annualDiscounts = useMemo(
    () => allDiscounts.filter((d) => d.tp === "A"),
    [allDiscounts]
  );
  const billDiscounts = useMemo(
    () => allDiscounts.filter((d) => d.tp === "B"),
    [allDiscounts]
  );
  const afterTaxDiscounts = useMemo(
    () => allDiscounts.filter((d) => d.tp === "C"),
    [allDiscounts]
  );

  // --- helpers to get the rows setter for a given type ---
  const getRowsAndSetter = useCallback(
    (type) => {
      if (type === "A") return { rows: annualRows, setRows: setAnnualRows, discounts: annualDiscounts };
      if (type === "B") return { rows: billRows, setRows: setBillRows, discounts: billDiscounts };
      return { rows: afterTaxRows, setRows: setAfterTaxRows, discounts: afterTaxDiscounts };
    },
    [annualRows, billRows, afterTaxRows, annualDiscounts, billDiscounts, afterTaxDiscounts]
  );

  // --- Available options for a row's select (exclude already-selected in same section) ---
  const getAvailableDiscounts = useCallback(
    (type, currentRowId) => {
      const { rows, discounts } = getRowsAndSetter(type);
      const usedIds = new Set(
        rows.filter((r) => r._rowId !== currentRowId && r.discount_id).map((r) => r.discount_id)
      );
      return discounts.filter((d) => !usedIds.has(d.id));
    },
    [getRowsAndSetter]
  );

  // --- Row CRUD ---
  const addRow = useCallback((type) => {
    const setRows =
      type === "A" ? setAnnualRows : type === "B" ? setBillRows : setAfterTaxRows;
    setRows((prev) => [
      ...prev,
      { _rowId: nextRowId(), discount_id: null, percentage_value: "", amount_value: "" },
    ]);
  }, []);

  const removeRow = useCallback((type, rowId) => {
    const setRows =
      type === "A" ? setAnnualRows : type === "B" ? setBillRows : setAfterTaxRows;
    setRows((prev) => prev.filter((r) => r._rowId !== rowId));
  }, []);

  const updateRowDiscount = useCallback((type, rowId, discountId) => {
    const setRows =
      type === "A" ? setAnnualRows : type === "B" ? setBillRows : setAfterTaxRows;
    setRows((prev) =>
      prev.map((r) => (r._rowId === rowId ? { ...r, discount_id: discountId } : r))
    );
  }, []);

  const updateRowValue = useCallback((type, rowId, field, value) => {
    const setRows =
      type === "A" ? setAnnualRows : type === "B" ? setBillRows : setAfterTaxRows;
    setRows((prev) =>
      prev.map((r) => {
        if (r._rowId !== rowId) return r;
        const updated = { ...r, [field]: value };
        if (parseFloat(value) > 0) {
          const otherField = field === "percentage_value" ? "amount_value" : "percentage_value";
          updated[otherField] = "";
        }
        return updated;
      })
    );
  }, []);

  // --- Reorder ---
  const moveRowUp = useCallback((type, rowId) => {
    const setRows =
      type === "A" ? setAnnualRows : type === "B" ? setBillRows : setAfterTaxRows;
    setRows((prev) => {
      const idx = prev.findIndex((r) => r._rowId === rowId);
      if (idx <= 0) return prev;
      const next = [...prev];
      [next[idx], next[idx - 1]] = [next[idx - 1], next[idx]];
      return next;
    });
  }, []);

  const moveRowDown = useCallback((type, rowId) => {
    const setRows =
      type === "A" ? setAnnualRows : type === "B" ? setBillRows : setAfterTaxRows;
    setRows((prev) => {
      const idx = prev.findIndex((r) => r._rowId === rowId);
      if (idx < 0 || idx >= prev.length - 1) return prev;
      const next = [...prev];
      [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
      return next;
    });
  }, []);

  // --- Basic form handlers ---
  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    setProfilingDetails((prev) => ({
      ...prev,
      [name]: type === "number" ? parseFloat(value) || 0 : value,
    }));
  };

  const handleProfileTypeChange = (option) => {
    setProfileType(option);
    setSelectedParty(null);
    setProfilingDetails((prev) => ({ ...prev, ps_Id: "" }));
    setAnnualRows([]);
    setBillRows([]);
    setAfterTaxRows([]);
  };

  const handlePartyChange = (party) => {
    setSelectedParty(party);
    setProfilingDetails((prev) => ({ ...prev, ps_Id: party?.ps_Id || "" }));
  };

  const handleCommissionConditionChange = (option) => {
    setProfilingDetails((prev) => ({ ...prev, agencommcond: option?.value || "" }));
  };

  // --- Submit ---
  const handleSubmit = async () => {
    try {
      const discountDetails = [];

      const processRows = (rows, discType) => {
        let orderCounter = 1;
        rows.forEach((row) => {
          if (!row.discount_id) return;
          const perc = parseFloat(row.percentage_value) || 0;
          const amt = parseFloat(row.amount_value) || 0;
          if (perc === 0 && amt === 0) return;

          discountDetails.push({
            discount_id: row.discount_id,
            discount_order: orderCounter++,
            discount_type: discType,
            percentage_value: perc > 0 ? perc : null,
            amount_value: amt > 0 ? amt : null,
            ag_status: 1,
          });
        });
      };

      processRows(annualRows, "A");
      processRows(billRows, "B");
      processRows(afterTaxRows, "C");

      const submitData = { ...profilingDetails, discountDetails };

      if (id) {
        await mutateUpdate({ id, data: submitData });
      } else {
        await mutateCreate(submitData);
      }
      onClose();
    } catch (error) {
      console.error("Operation failed:", error);
    }
  };

  // --- Populate form when editing ---
  useEffect(() => {
    if (data) {
      setProfilingDetails({
        ps_Id: data.ps_Id || "",
        date: data.date
          ? new Date(data.date).toISOString().split("T")[0]
          : new Date().toISOString().split("T")[0],
        noofdaysCD: data.noofdaysCD || 0,
        noofdayspay: data.noofdayspay || 0,
        intprcnt: data.intprcnt || 0,
        terms: data.terms || "",
        agentcom: data.agentcom || 0,
        agencommcond: data.agencommcond || "",
        defaultextn: data.defaultextn || 0,
        ag_status: data.ag_status ?? 1,
      });

      if (data.discountDetails && Array.isArray(data.discountDetails)) {
        const annual = [];
        const bill = [];
        const afterTax = [];

        data.discountDetails
          .sort((a, b) => a.discount_order - b.discount_order)
          .forEach((detail) => {
            const row = {
              _rowId: nextRowId(),
              discount_id: detail.discount_id,
              percentage_value:
                detail.percentage_value != null ? String(detail.percentage_value) : "",
              amount_value:
                detail.amount_value != null ? String(detail.amount_value) : "",
            };

            if (detail.discount_type === "A") annual.push(row);
            else if (detail.discount_type === "B") bill.push(row);
            else if (detail.discount_type === "C") afterTax.push(row);
          });

        setAnnualRows(annual);
        setBillRows(bill);
        setAfterTaxRows(afterTax);
      }

      if (data.ps_Id) {
        const partyType = data.party?.type || data.ps_Id[0];
        const detectedProfileType =
          partyType === "C"
            ? { value: "C", label: "Customer" }
            : { value: "V", label: "Vendor" };

        setProfileType(detectedProfileType);
        setSelectedParty({
          ps_Id: data.ps_Id,
          name: data.party?.name,
          type: data.party?.type || detectedProfileType.value,
        });
      }
    }
  }, [data]);

  return {
    profileType,
    selectedParty,
    profilingDetails,
    isLoadingProfiling,
    isLoadingDiscounts,
    isCreating,
    isUpdating,
    annualDiscounts,
    billDiscounts,
    afterTaxDiscounts,
    annualRows,
    billRows,
    afterTaxRows,
    handleInputChange,
    handleProfileTypeChange,
    handlePartyChange,
    handleCommissionConditionChange,
    getAvailableDiscounts,
    addRow,
    removeRow,
    updateRowDiscount,
    updateRowValue,
    moveRowUp,
    moveRowDown,
    handleSubmit,
  };
};
